/**
 * registry.js — el contenido de la Academia.
 *
 * Datos puros, sin JSX: la pantalla (`AcademyPage`) y el cajón de ayuda
 * (`HelpDot`) sólo saben pintar lo que hay aquí.
 *
 * Estructura:  capítulo → tema → bloques
 * Tipos de bloque: text | steps | shot | note | sandbox | live
 *
 * Reglas (ver docs/ACADEMIA_CMDB.md):
 *   · `menuKey` enlaza el tema con una entrada de `menuConfig.js`. El test
 *     `registry.test.js` obliga a que toda entrada del menú esté documentada
 *     o explícitamente en `COVERAGE_PENDING`.
 *   · `revisedOn` es la fecha de la última revisión real del contenido.
 *   · Si cambia la pantalla, cambia el tema en el mismo commit.
 */

const text = (body) => ({ type: 'text', body });
const steps = (items) => ({ type: 'steps', items });
const shot = (src, caption) => ({ type: 'shot', src, caption, alt: caption });
const tip = (body) => ({ type: 'note', tone: 'tip', body });
const warn = (body) => ({ type: 'note', tone: 'warn', body });
const sandbox = (demo) => ({ type: 'sandbox', demo });
const live = (label, to) => ({ type: 'live', label, to });

const REVISED = '2026-10-31';

export const ACADEMY_CHAPTERS = [
  // ───────────────────────────────────────────────────────────────────────
  {
    id: 'menu',
    title: 'El menú y la navegación',
    summary: 'Cómo está ordenada la aplicación, dónde vive cada cosa y cómo dejar el menú a tu gusto.',
    icon: '🧭',
    topics: [
      {
        id: 'menu.order',
        title: 'Cómo está ordenado el menú',
        summary: 'Favoritos arriba, cinco títulos de sección y el contenido dentro de cada sección.',
        menuKey: 'dashboard',
        route: '/dashboard',
        revisedOn: REVISED,
        blocks: [
          text(
            'El menú **no** enseña de golpe las ochenta entradas de la aplicación. Arriba van tus ' +
            '**favoritos** y, debajo, los **cinco títulos de sección**. Al entrar en una sección se ' +
            'despliega su contenido y aparece **‹ Todas las secciones** para volver.'
          ),
          shot('menu/01-menu.png', 'El menú por defecto: favoritos (si tienes) y las cinco secciones.'),
          steps([
            '**Sin sección**: Dashboard y Documentación, siempre a la vista.',
            '**INFRAESTRUCTURA**: ubicaciones, contactos y los equipos que están en un rack o en una mesa.',
            '**CONECTIVIDAD**: direcciones IP, WiFi, operadores, cámaras y DNS.',
            '**VIRTUALIZACIÓN**: hipervisores, clusters, datastores y máquinas virtuales.',
            '**SERVICIOS**: el catálogo, los mapas de dependencias, backups, bases de datos y licencias.',
            '**GESTIÓN**: roles, nomenclatura, catálogo de dispositivos, ajustes y esta Academia.',
          ]),
          shot(
            'menu/02-secciones.png',
            'Dentro de INFRAESTRUCTURA: enlaces sueltos (Ubicaciones, Contactos) y grupos (Infraestructura Física, Equipos de Red, Cableado).'
          ),
          tip(
            'Dentro de una sección, los **grupos** se pliegan por separado. El estado se recuerda: al volver, ' +
            'lo encuentras como lo dejaste.'
          ),
          sandbox('menu'),
          live('Abrir el menú de verdad', '/dashboard'),
        ],
      },
      {
        id: 'menu.sections',
        title: 'Menú «Por secciones» o «Clásico»',
        summary: 'Los dos estilos de menú y por qué el nuevo se llama «Por secciones».',
        menuKey: 'dashboard',
        route: '/dashboard',
        revisedOn: REVISED,
        blocks: [
          text(
            'En **Personalizar menú → Menú y visibilidad → Estilo del menú** eliges entre dos formas ' +
            'de navegar. Se guarda en tu preferencia, no en la de la empresa.'
          ),
          steps([
            '**Por secciones** (recomendado): sólo los títulos en el menú; al entrar en una sección se ' +
            'despliega su contenido, con tus favoritos arriba.',
            '**Clásico**: todas las entradas a la vista, agrupadas en acordeones. Es el menú de siempre.',
          ]),
          shot(
            'menu/06-ocultar.png',
            'Los dos estilos, en Personalizar menú. «Por secciones» es el que viene por defecto.'
          ),
          tip('En «Por secciones» el menú ocupa menos y se lee mejor; en «Clásico» llegas a todo con un clic.'),
          warn(
            'Si alguien te enseña un menú con todas las entradas desplegadas, está en «Clásico». No es la ' +
            'versión nueva: se cambia en el mismo diálogo.'
          ),
          sandbox('menu'),
        ],
      },
      {
        id: 'menu.favorites',
        title: 'Favoritos: tus atajos',
        summary: 'La estrella del topbar y el bloque FAVORITOS de arriba.',
        menuKey: 'dashboard',
        route: '/dashboard',
        revisedOn: REVISED,
        blocks: [
          text(
            'Los favoritos son **tuyos**, no de la empresa. Se añaden desde cualquier pantalla con la ' +
            '**estrella** del topbar y aparecen en el bloque **FAVORITOS**, encima de las secciones.'
          ),
          steps([
            'Entra en la pantalla que quieras tener a mano (un listado, una ficha…).',
            'Pulsa la **estrella** del topbar: verás «Añadir solo a esta empresa» o «Añadir a todas mis empresas».',
            'El acceso aparece arriba del menú, con su **×** para quitarlo.',
            'En **Personalizar menú → Favoritos** cambias el orden con las flechas de cada entrada.',
          ]),
          shot('menu/03-favoritos.png', 'La estrella del topbar elige el ámbito del acceso directo.'),
          shot('menu/04-favoritos-menu.png', 'El bloque FAVORITOS va siempre por encima de las secciones.'),
          warn(
            'Hay un máximo de **20 accesos directos** por ámbito. Si llegas al tope, quita uno antes de añadir otro.'
          ),
          sandbox('menu'),
        ],
      },
      {
        id: 'menu.hide',
        title: 'Ocultar lo que no usas',
        summary: 'Personalizar menú: qué quitas de tu vista y qué no puedes quitarte.',
        menuKey: 'dashboard',
        route: '/dashboard',
        revisedOn: REVISED,
        blocks: [
          text(
            'En **Personalizar menú → Menú y visibilidad** desmarcas las entradas que no quieres ver en la ' +
            'empresa activa. No borra nada ni cambia permisos: sólo deja de pintarlas para ti.'
          ),
          steps([
            'Abre **Personalizar menú** desde **Añadir favoritos** o **Editar favoritos** del menú lateral.',
            'Desmarca lo que no uses. Cada grupo tiene su **Ocultar todo** para ir rápido.',
            'Arriba se ve el recuento: «Se verán **80 de 81** entradas del menú».',
            'Si te has pasado, **Mostrar todo** devuelve lo que la empresa permita.',
          ]),
          tip('Tu preferencia sólo puede quitar: lo que la empresa ha ocultado no se puede añadir desde aquí.'),
          shot('menu/05-personalizar.png', 'Personalizar menú: favoritos y orden de los accesos directos.'),
          sandbox('menu'),
        ],
      },
      {
        id: 'menu.find',
        title: 'Encontrar cualquier cosa',
        summary: 'Buscador global, migas de pan y lanzador de aplicaciones.',
        menuKey: 'dashboard',
        route: '/dashboard',
        revisedOn: REVISED,
        blocks: [
          text(
            'Cuando no recuerdas dónde estaba algo, no hace falta recorrer el menú: hay tres atajos.'
          ),
          steps([
            '**Buscador global** (arriba, atajo **Ctrl+K**): busca equipos, servicios y documentos de la empresa activa.',
            'El botón **🌐** de al lado busca también en el resto de empresas que administras.',
            '**Migas de pan**: la línea del contenido dice en qué punto estás y te deja subir.',
            '**Lanzador de aplicaciones** (la cuadrícula de arriba a la izquierda): abre el resto de apps del IdP.',
          ]),
          tip(
            'Ojo: el lanzador (cuadrícula) es el selector de módulos con **otra** agrupación, la de siempre. ' +
            'El menú ordenado por secciones es el del lateral.'
          ),
          live('Probar el buscador', '/dashboard'),
        ],
      },
      {
        id: 'menu.mobile',
        title: 'En el móvil y en tablet',
        summary: 'Qué se retira de la barra y cómo se entra al menú.',
        menuKey: 'dashboard',
        route: '/dashboard',
        revisedOn: REVISED,
        blocks: [
          text(
            'La aplicación está pensada también para moverse por una planta con el móvil: el menú se ' +
            'recoge en un botón y la barra superior se queda sólo con lo imprescindible.'
          ),
          steps([
            'En el móvil, el menú lateral se abre con el **botón ☰**.',
            'La barra superior esconde los textos y se queda con iconos.',
            'Las fichas ponen el panel de actividad **plegado abajo**, para que el formulario no quede en ' +
            'una tira de 70 px.',
          ]),
          warn('Los gestos de dibujo (lienzos y diseñadores) siguen pidiendo pantalla grande: en móvil se consultan, no se diseñan.'),
        ],
      },
      {
        id: 'academia.how',
        title: 'Cómo usar esta Academia',
        summary: 'Dónde está el «?», qué es una sandbox y cómo se busca un tema.',
        menuKey: 'academia',
        route: '/academia',
        revisedOn: REVISED,
        blocks: [
          text(
            'La Academia es la misma documentación que tienes a mano en la pantalla: cada ficha y cada ' +
            'listado llevan un **«?»** que abre el tema exacto que necesitas, sin salir de donde estás.'
          ),
          steps([
            'Pulsa el **«?»** de la cabecera de una ficha para abrir su ayuda en un cajón lateral.',
            'Si quieres el tema completo, pulsa **Ver en la Academia**.',
            'Usa el **buscador** de la Academia con la palabra que recuerdes («rack», «exportar»…).',
            'Las **sandbox** son de mentira: puedes equivocarte sin miedo, no tocan tus datos.',
          ]),
          tip('Si un tema no existe, es un fallo de la aplicación: el «?» debe existir siempre.'),
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────
  {
    id: 'racks',
    title: 'Racks',
    summary: 'El armario físico: cómo se entra, qué cuenta el listado, qué hay en la ficha y cómo se diseña.',
    icon: '🗄️',
    topics: [
      {
        id: 'datacenter.menu',
        title: 'El módulo CPD',
        summary: 'Racks, PDUs, SAI, patch panels y los planos están en una sola entrada del menú.',
        menuKey: 'datacenter',
        route: '/infrastructure/racks',
        revisedOn: '2026-11-01',
        blocks: [
          text(
            'En el menú lateral, **INFRAESTRUCTURA** ya no tiene seis entradas sueltas de armario. ' +
            'Todo eso vive ahora en **CPD**, y al entrar el menú se queda solo con lo del ' +
            'módulo, con dos salidas arriba: **Todas las secciones** e **Infraestructura**.'
          ),
          steps([
            '**Racks**: el armario físico, sus unidades y su diseñador.',
            '**PDUs**: regletas de alimentación y sus tomas.',
            '**SAI (torre y rack)**: los equipos de respaldo eléctrico.',
            '**Patch Panels**: el cableado pasivo y sus puertos.',
            '**Canvas Alimentación**: qué PDU cuelga de qué SAI y qué equipo de qué toma.',
            '**Diagrama Datacenter**: la vista general del armario, para imprimir o proyectar.',
          ]),
          tip(
            'Las seis siguen existiendo para el buscador y los favoritos: si tienes **Racks** en ' +
            'favoritos, la estrella te lleva igual que antes. Lo que cambia es que ya no ocupan ' +
            'seis líneas del menú.'
          ),
          warn(
            'Las direcciones no cambian: `/infrastructure/pdus` sigue siendo la lista de PDUs. ' +
            'Si tenías un marcador, funciona; lo único distinto es el menú que verás al llegar.'
          ),
        ],
      },
      {
        id: 'racks.entry',
        title: 'Cómo se entra a Racks',
        summary: 'El camino en el menú y la ruta directa.',
        menuKey: 'racks',
        route: '/infrastructure/racks',
        revisedOn: REVISED,
        blocks: [
          text('Los racks viven en **INFRAESTRUCTURA → Racks**.'),
          steps([
            'Abre el grupo **Infraestructura física** del menú.',
            'Pulsa **Racks**.',
            'Si lo usas a diario, añádelo a favoritos con la estrella del topbar.',
          ]),
          live('Abrir el listado de racks', '/infrastructure/racks'),
        ],
      },
      {
        id: 'racks.list',
        title: 'Qué cuenta el listado de racks',
        summary: 'Unidades ocupadas, elementos montados, borradores y las acciones de cada fila.',
        menuKey: 'racks',
        route: '/infrastructure/racks',
        revisedOn: REVISED,
        blocks: [
          text(
            'Cada fila es un armario. La columna **Ocupación** cuenta las unidades **U** pilladas en la cara ' +
            'frontal respecto a la altura total; en **Acciones**, la caja muestra cuántos elementos hay ' +
            'montados (no todos ocupan U: un PDU vertical, por ejemplo).'
          ),
          shot('racks/01-listado.png', 'El listado: ocupación por rack, fabricante, estado y acciones de fila.'),
          steps([
            '**Vistas**: **Lista** (tabla), **Fichas** (tarjetas) y **Frontal** (los armarios de frente, con cada equipo en su U).',
            '**Filtros**: producción y diseño, ubicación, estado, ocupación y altura.',
            '**Nuevo Rack** abre la ficha en blanco.',
            'Clic en cualquier punto de la fila → abre la ficha **en modo lectura**.',
            'Botones de fila: **ver el rack** (vista gráfica), **ver elementos montados**, copiar, editar y eliminar.',
          ]),
          tip('Un rack en **borrador** se puede diseñar sin que cuente en el inventario ni consuma numeración.'),
          warn(
            'Borrar un rack es **físico**: se lleva por delante los elementos montados en él. El diálogo te '
            + 'dice cuántos antes de confirmar.'
          ),
        ],
      },
      {
        id: 'racks.form',
        title: 'La ficha del rack',
        summary: 'Cabecera, badge, chips, el pager y las pestañas General y Elementos.',
        menuKey: 'racks',
        route: '/infrastructure/racks',
        revisedOn: REVISED,
        blocks: [
          text(
            'La ficha del rack es la **ficha canónica** del CMDB: el resto de fichas copian su ' +
            'comportamiento. Cabecera con el nombre editable, el **«?»** de ayuda y el badge de estado; ' +
            'debajo, el pager y las pestañas.'
          ),
          shot('racks/02-ficha.png', 'Cabecera con «?», badge Activo y chips de Docs; debajo, ocupación en U y tarjetas.'),
          steps([
            '**General**: identificación, dimensiones, hardware y estado, en tarjetas.',
            '**Elementos**: lo que hay montado, con su unidad de inicio y su altura.',
            '**Diseño** abre la vista frontal y trasera del armario.',
            'El **‹ n / total ›** de arriba recorre las fichas sin volver al listado. El orden queda fijado al abrir y **no cambia al guardar**.',
            'Si pasas de ficha con cambios sin guardar, la app pregunta: **Guardar y continuar**, **Descartar** o **Cancelar**. Si guardas, sigues en edición y en la misma pestaña.',
            'El **✓** de la cabecera y la barra inferior guardan; el ✓ se apaga si no hay cambios.',
          ]),
          tip('El nombre se edita en la propia cabecera: sin caja en reposo, ✏️ al pasar el ratón, Enter guarda y Esc descarta.'),
          warn('La numeración del rack no se inventa: el botón de sugerir código usa la nomenclatura de la empresa.'),
        ],
      },
      {
        id: 'racks.designer',
        title: 'Diseñar el rack',
        summary: 'Vista frontal y trasera, colocar equipos por unidad y pasar el diseño a producción.',
        menuKey: 'racks',
        route: '/infrastructure/racks',
        revisedOn: REVISED,
        blocks: [
          text(
            'El diseñador es la vista gráfica del armario. Se abre con **Diseño** desde la ficha o con el ' +
            'botón de la fila del listado.'
          ),
          shot('racks/03-disenador.png', 'Diseño: contadores F/R, pestañas Colocación y Cableado, alta rápida y las dos caras.'),
          steps([
            'Arriba, los contadores: **42U TOTAL**, **F 15U FRONTAL**, **R 5U TRASERA** y **2 FULL DEPTH**.',
            '**Colocación**: arrastra un equipo a una unidad, o usa **Añadir equipo** (Switch, Patch Panel, PDU).',
            '**Añadir accesorio**: tapa ciega, pasahilos y bandeja no ocupan U.',
            'Lo que no tiene unidad va a **Pendientes sin U**, arriba, hasta que lo coloques.',
            '**Cableado** es la otra pestaña: aquí se ven los latiguillos, no las unidades.',
          ]),
          sandbox('rack'),
          tip('Frontal y trasera se editan a la vez: un equipo puede ocupar U en una cara y no en la otra.'),
        ],
      },
      {
        id: 'racks.elements',
        title: 'Qué elementos pueden ir en un rack',
        summary: 'No todo lo que hay en el inventario ocupa unidades.',
        menuKey: 'racks',
        route: '/infrastructure/racks',
        revisedOn: REVISED,
        blocks: [
          text('Pueden montarse en un rack, ocupando unidades:'),
          steps([
            '**Servidores** y **chasis multinodo**.',
            '**Switches**, **firewalls** y **routers**.',
            '**SAI de rack** (los de torre, no) y **almacenamiento**.',
            '**PDUs** y **patch panels** (pueden ir en vertical, sin ocupar U).',
            '**Cámaras IP** y grabadores, cuando van en armario.',
          ]),
          tip('Los equipos de sobremesa (estaciones de trabajo, impresoras, teléfonos) no van al rack: van a la ubicación.'),
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────
  {
    id: 'listados',
    title: 'Listados',
    summary: 'El comportamiento común de todas las tablas: columnas, exportación, importación y móvil.',
    icon: '🗂️',
    topics: [
      {
        id: 'listados.columns',
        title: 'Mover y ocultar columnas',
        summary: 'El engranaje de la cabecera de la tabla, arrastrar para ordenar y «Restablecer columnas».',
        menuKey: 'racks',
        route: '/infrastructure/racks',
        revisedOn: REVISED,
        blocks: [
          text(
            'Todas las tablas llevan el mismo engranaje al final de la cabecera: **Mostrar / ocultar ' +
            'columnas**. Lo que elijas se recuerda en tu navegador para esa lista concreta: no se lo ' +
            'cambias a nadie más.'
          ),
          shot('listados/01-columnas.png', 'El engranaje abre la lista de columnas: arrastra para ordenar y marca para ver.'),
          steps([
            'Pulsa el **engranaje** de la cabecera de la tabla (no está en la barra de filtros).',
            'Marca o desmarca columnas; el cambio es inmediato.',
            'Arrastra por el **⠿** para cambiar el orden.',
            '**Restablecer columnas** vuelve al orden y visibilidad de fábrica.',
            'Pulsa fuera o **Esc** para cerrar.',
          ]),
          sandbox('list'),
          tip('La barra de arriba siempre va igual: buscador → filtros → importar → exportar → vistas.'),
          tip(
            'Los **filtros nacen plegados** en todos los listados y en **cualquier resolución**: solo ' +
            'se ve el buscador y el botón **Filtros**. La tabla se queda con todo el alto para los ' +
            'datos, y el botón dice cuántos filtros hay puestos aunque esté cerrado.'
          ),
        ],
      },
      {
        id: 'listados.export',
        title: 'Exportar a Excel',
        summary: 'Qué se exporta y con qué filtros.',
        menuKey: 'racks',
        route: '/infrastructure/racks',
        revisedOn: REVISED,
        blocks: [
          text('**Exportar Excel** baja lo que estás viendo, no la tabla entera: aplica el buscador y los filtros activos.'),
          steps([
            'Filtra o busca hasta dejar en pantalla lo que quieres.',
            'Pulsa **Exportar Excel**.',
            'El archivo lleva cabeceras legibles, no códigos internos.',
          ]),
          warn('Si exportas sin filtros en una empresa grande, el archivo puede tardar. Filtra primero.'),
        ],
      },
      {
        id: 'listados.import',
        title: 'Importar desde Excel o CSV',
        summary: 'El importador común: fichero, mapeo de columnas y resultado por fila.',
        menuKey: 'racks',
        route: '/infrastructure/racks',
        revisedOn: REVISED,
        blocks: [
          text(
            'Donde el listado lo permite, la cabecera muestra **Importar**. Sirve para dar de alta lotes ' +
            'sin teclear fila a fila. El fichero **no se sube**: se lee en tu navegador y sólo viajan las ' +
            'filas ya mapeadas.'
          ),
          steps([
            'Pulsa **Importar** en la cabecera del listado.',
            'Arrastra el **Excel o CSV** (o selecciónalo); se muestran las cabeceras detectadas.',
            'Revisa el **mapeo campo ↔ columna**. Se empareja solo por nombre; lo que no cuadre se elige a mano.',
            'Mira la **vista previa** de las primeras filas y decide si actualizar los registros existentes.',
            'Al terminar verás **creados, actualizados y omitidos**, con los errores fila a fila.',
          ]),
          tip('Si una columna no existe en el fichero, se queda en blanco: no hay que rellenarla a la fuerza.'),
          warn(
            'Revisa el selector de empresa antes de importar. Un fichero cargado en la empresa equivocada ' +
            'hay que deshacerlo a mano.'
          ),
        ],
      },
      {
        id: 'listados.rows',
        title: 'Abrir, copiar, editar y borrar',
        summary: 'Las acciones de fila y la barra de selección.',
        menuKey: 'racks',
        route: '/infrastructure/racks',
        revisedOn: REVISED,
        blocks: [
          text(
            'Clic en la fila abre la ficha **en lectura**; para modificar hay que pulsar **Editar**. Así no se ' +
            'cambia nada sin querer al ir a mirar.'
          ),
          steps([
            'Botones por fila, siempre en el mismo orden: acciones propias · **Copiar** · **Editar** · **Eliminar**.',
            'Copiar pregunta el nombre nuevo y explica qué se copia y qué no.',
            'Eliminar abre un diálogo que se cierra con **Esc** o pulsando fuera.',
            'Con la casilla de la izquierda seleccionas varias filas y aplicas una **acción masiva**.',
          ]),
          tip('La fila en la que has entrado desde otro módulo se resalta unos segundos; no estás perdido.'),
        ],
      },
      {
        id: 'listados.agent_presence',
        title: 'El agente en línea, en tiempo real',
        summary: 'La columna «Agente» ya no espera cinco minutos: cambia en segundos.',
        menuKey: 'racks',
        route: '/infrastructure/racks',
        revisedOn: '2026-11-02',
        blocks: [
          text(
            'La columna **Agente** de servidores y estaciones de trabajo ya no espera al latido de cinco ' +
            'minutos. El agente mantiene un canal abierto con el CMDB y, cuando el equipo se enciende, se ' +
            'apaga o pierde la red, el estado cambia en **uno o dos segundos**.'
          ),
          steps([
            '**● Online**: el agente tiene su canal abierto con el CMDB ahora mismo.',
            '**● Offline**: el canal se ha cerrado (equipo apagado, servicio parado o sin red).',
            '**Sin agente**: ese equipo nunca ha instalado el agente; no hay nada que escuchar.',
            'Pasa el ratón por el icono: dice cuándo se le vio por última vez y si el canal va por WebSocket o por polling.',
          ]),
          tip(
            'Saber esto cambia lo que puedes hacer desde la consola: con el equipo **Online**, el ' +
            '**rescan** del agente se ejecuta al momento; con **Offline**, el comando queda en cola ' +
            'hasta que vuelva.'
          ),
          text(
            'El resumen de **Equipos** también es en vivo: el contador **Con agente** y el anillo de ' +
            'estado se recalculan solos cuando un equipo entra o se cae, sin recargar la página.'
          ),
          warn(
            'Un portátil suspendido tarda unos segundos más en caer: el canal queda medio abierto ' +
            'hasta que expira el latido de la red.'
          ),
        ],
      },
      {
        id: 'listados.agent_update',
        title: 'Actualizar el agente desde la ficha',
        summary: 'El agente se actualiza desde la consola, sin tocar el equipo, y la ficha lo confirma.',
        menuKey: 'racks',
        route: '/infrastructure/racks',
        revisedOn: '2026-11-02',
        blocks: [
          text(
            'En la ficha del equipo, el menú **Sincronizar** incluye **Actualizar agente**. Al pulsarlo, ' +
            'el CMDB encola la orden y el agente descarga e instala la versión publicada en silencio; no ' +
            'hay que entrar al equipo ni reinstalar a mano.'
          ),
          steps([
            'El agente descarga **el mismo formato** con el que se instaló (MSI o EXE), así no aparecen dos entradas en «Aplicaciones».',
            'Mientras se ejecuta, la cabecera de la ficha muestra **Actualizando…**.',
            'Cuando el equipo vuelve a conectar con la versión nueva, el estado pasa a **Actualizado** con el número de versión.',
            'Si la versión publicada es la misma que ya tiene —o más antigua— no se reinstala: queda en **Agente al día**.',
            '**Sin confirmar** significa que el instalador se preparó pero el equipo todavía no ha reportado la versión nueva.',
            'Si algo falla, el badge queda en **Actualización fallida** y el motivo aparece al pasar el ratón.',
          ]),
          tip(
            'La ficha no canta «actualizado» en el momento de encolar: espera al latido con la versión ' +
            'nueva. Es la única forma de distinguir una instalación buena de un instalador que se lanzó y ' +
            'no llegó a terminar.'
          ),
          tip(
            'El instalador se lanza **fuera del servicio** y, al terminar, el propio equipo vuelve a ' +
            'arrancar el agente. Así una actualización no deja el equipo sin latido aunque el instalador ' +
            'se corte a medias. Si aun así el equipo no vuelve en unos minutos, el servicio está parado y ' +
            'hay que arrancarlo desde el propio equipo.'
          ),
          warn(
            'El artefacto publicado es **global**: se aplica a todo el parque. Antes de publicar una ' +
            'versión de prueba, hazlo en un equipo y comprueba el resultado.'
          ),
          tip(
            'En **Ajustes → Agente CMDB → Distribución** el fichero se puede **arrastrar** sobre la ' +
            'tarjeta, sin buscar la ruta en el diálogo. Soltarlo solo lo deja seleccionado: hay que ' +
            'pulsar **Publicar** para que el parque empiece a verlo. Así un arrastre accidental no ' +
            'publica una versión a medias.'
          ),
          tip(
            'Este tema no lleva sandbox: para verlo hace falta un agente real y un instalador publicado. ' +
            'Una demo sin servidor no enseñaría nada que no cuenten estos pasos.'
          ),
        ],
      },
      {
        id: 'listados.agent_activity',
        title: 'El log del agente y sus ejecuciones',
        summary: 'La pestaña «Agente» del equipo enseña qué se le pidió y qué contestó.',
        menuKey: 'racks',
        route: '/infrastructure/racks',
        revisedOn: '2026-11-01',
        blocks: [
          text(
            'La pestaña **Agente** de la ficha concentra el diagnóstico del equipo sin entrar por ' +
            'RDP: estado y versión del agente, **todas las ejecuciones** que se le han encolado y la ' +
            'salida de cada una.'
          ),
          steps([
            'Cada fila es una ejecución: **Rescan**, **Comando**, **Actualizar agente**…, con su estado (En cola, Ejecutando, Hecho, Error) y lo que tardó.',
            'Pulsa una fila y se abre su salida completa: `stdout`, `stderr` y el código de salida, sin recortes.',
            'Mientras algo está en cola o ejecutándose, la lista se refresca sola cada 8 segundos.',
            '**Rescan completo** pide al agente que vuelva a recoger sistema, discos, volúmenes y tarjetas de red y los suba al CMDB.',
            '**Actualizar agente** descarga la versión publicada en el CMDB y la instala en el equipo; el servicio se reinicia solo. Funciona en agentes **2.6.6 o superior**.',
            '**Ver log del agente** pide al equipo las últimas líneas de `cmdb-agent.log` y las deja como una ejecución más.',
            'Arriba del todo está la **consola de acciones remotas**: rescan, actualizar agente, log y comando libre. El formulario de «Nueva acción» viene plegado y el historial solo enseña lo que está **en curso**; el resto, tras **Ver historial completo**.',
            'El registro de ejecuciones del agente sigue la misma regla: si no hay nada en curso se queda en una línea, con el botón para abrir el historial. Con un comando en marcha, ese comando y su salida son lo único que ocupa sitio.',
          ]),
          tip(
            'El log del agente **rota solo**: 20 MB por fichero y tres copias (`cmdb-agent.log.1`, `.2`, `.3`). ' +
            'En un equipo con el servicio años en marcha eso son varios meses de latidos sin comer disco.'
          ),
          tip(
            'La fila de **Actualizar agente** nunca sale muda: si el proceso no devuelve texto, se resume ' +
            'con el modo, la versión de origen, la de destino y el instalador usado. Así se distingue ' +
            '«descargado» de «instalado» sin entrar al equipo.'
          ),
          tip(
            'Junto a la versión instalada aparece **→ vX.Y.Z** en ámbar cuando hay una más nueva publicada, ' +
            'y la pestaña **Hardware** lista los componentes que ha detectado el agente (CPU, memoria, ' +
            'discos, tarjetas…) sin abrir el editor de componentes.'
          ),
          warn(
            'Si el log tiene más de 20 MB no se pierde: al arrancar el siguiente ciclo se guarda como `.1`. ' +
            'Para ver lo más reciente basta con la cola del fichero principal.'
          ),
          tip(
            'Si el disco, la RAM o el software no cuadran, este es el primer sitio donde mirar: el ' +
            'log dice qué colector falló, no solo que faltan datos.'
          ),
          tip(
            'La misma pestaña **Agente** está en la ficha de una **máquina virtual**, con idéntico ' +
            'comportamiento: rescan, actualizar agente, log y comandos. Ahí el agente es el que se ' +
            'instala **dentro** del sistema invitado; el del hipervisor no da software, ni roles, ' +
            'ni Active Directory, ni parches.'
          ),
          warn(
            'La actividad se lee de la cola de comandos y solo la ve un administrador. Un editor ve ' +
            'la pestaña, pero sin historial.'
          ),
          warn(
            'Un agente anterior a la **2.6.6** no sabe actualizarse solo: pide un subcomando que su ' +
            'servicio no tiene y contesta «Usage: cmdb-service.exe install|uninstall|start|stop|status». ' +
            'La ficha lo avisa con la pill **Sin autoactualización** y no llega a encolar nada. Esos ' +
            'equipos se ponen al día con el **MSI** o el **EXE** publicado (Ajustes → Agente CMDB) o ' +
            'por GPO; desde la 2.6.6 el botón funciona sin tocar el equipo.'
          ),
          tip(
            'El resumen también bebe del agente: el KPI **Disco** y la tarjeta «Cómputo y SO» suman ' +
            'los discos detectados, y el usuario que reporta el inventario pasa a ser el **asignado** ' +
            'del equipo si no había ninguno puesto a mano.'
          ),
        ],
      },
      {
        id: 'agente.paquetes',
        title: 'Paquetes: instalar software con Chocolatey',
        summary: 'Catálogo predefinido, despliegue por equipos y qué hacer cuando un paquete falla.',
        menuKey: 'agent-settings',
        route: '/agent-settings',
        revisedOn: '2026-11-01',
        blocks: [
          text(
            'Chocolatey es el gestor de paquetes de Windows: instala, actualiza y desinstala software ' +
            'con un comando, sin ventanas y sin entrar por RDP. La CMDB lo usa para repartir software ' +
            'a los equipos que tienen agente.'
          ),
          steps([
            'Arriba, los **KPIs** del parque: paquetes en catálogo, equipos con agente e instalaciones OK, en curso y con error.',
            'En **Destinos**, busca equipos por nombre y márcalos, o deja **Todos los equipos con agente**. Solo se listan equipos que tienen agente; los que llevan una versión anterior a la mínima salen con la pill **antiguo**.',
            'En **Agente → Paquetes**, abre **Ver catálogo**. Hay un centenar de paquetes ya definidos con su nombre, fabricante y categoría: no hay que teclear ids.',
            'Marca los que quieras y pulsa **Añadir seleccionados**, o **Añadir recomendados** para traerte de golpe el software de uso común.',
            'Pulsa **Desplegar** en la fila del paquete. También puedes desplegar directamente desde el catálogo predefinido: el paquete se añade solo.',
            'La tarjeta **Instalado en el parque** resume, por paquete, cuántos equipos lo tienen, cuántos fallaron y cuándo fue el último despliegue.',
            'En **Despliegues** filtra por estado, paquete o equipo, **reintenta todos los errores** de una vez o **exporta a Excel** todo lo desplegado.',
            'El agente ejecuta `choco upgrade <paquete> --install-if-not-installed` en segundo plano y sigue latiendo mientras instala.',
          ]),
          tip(
            'En la ficha de un equipo, la pestaña **Software** tiene el mismo catálogo limitado a ese ' +
            'equipo: sirve para instalar Chrome, 7-Zip o Logi Options+ en un solo PC sin pasar por el panel global.'
          ),
          warn(
            'Un paquete a la vez por equipo. Dos instaladores de MSI simultáneos es pedir problemas: el ' +
            'segundo despliegue queda en error con un mensaje claro.'
          ),
          warn(
            'El catálogo predefinido son paquetes de la comunidad de Chocolatey. Para software ' +
            'propietario o con versión fija (un driver de Logitech, un ERP) conviene tener un repositorio ' +
            'interno: el paquete público puede ir por detrás de la versión ya instalada y entonces el ' +
            'instalador rechaza el downgrade (error 1287).'
          ),
          tip(
            'Requiere agente **2.6.11** o superior. Si un equipo está por debajo, el despliegue queda en ' +
            'error con «Unknown command»: actualízalo desde la ficha.'
          ),
          warn(
            'El agente corre como **SYSTEM**, así que un paquete que instala «por usuario» acaba en el ' +
            'perfil de la cuenta de servicio (`C:\\Windows\\System32\\config\\systemprofile\\AppData`) y ' +
            'nadie lo ve en su menú Inicio. Es lo que pasa con Vivaldi de la comunidad: Chocolatey dice ' +
            'que está instalado, y lo está, pero para SYSTEM. Para el parque hace falta un paquete ' +
            'propio que instale a nivel de máquina (`--system-level` en los instaladores de Chromium).'
          ),
          tip(
            'Desde la ficha, la pestaña **Actualizaciones** tiene la tarjeta **Software de terceros ' +
            '(Chocolatey)**: un botón lanza `choco upgrade all` en ese equipo y deja al día todo lo que ' +
            'se instaló con Chocolatey, sin tocar Windows Update.'
          ),
        ],
      },
      {
        id: 'listados.workstations_menu',
        title: 'El menú de Estaciones de trabajo',
        summary: 'Al entrar en Equipos el menú lateral se queda con el módulo y dos salidas hacia arriba.',
        menuKey: 'workstations',
        route: '/infrastructure/workstations',
        revisedOn: '2026-11-01',
        blocks: [
          text(
            '**Estaciones de trabajo** dejó de ser una entrada suelta: es un **módulo**. En cuanto ' +
            'entras en cualquiera de sus pantallas, el menú lateral deja de enseñar el árbol entero ' +
            'y muestra solo lo del módulo, con dos accesos arriba del todo.'
          ),
          steps([
            '**Todas las secciones**: sale del módulo y vuelve a la raíz del menú.',
            '**Infraestructura**: sale del módulo y abre la sección de la que cuelga, con sus grupos desplegados.',
            '**Dashboard**: los KPIs del parque, el reparto por sistema y el estado del agente.',
            '**Equipos**: el listado con filtros, informe en PDF/Excel y la ficha de cada equipo.',
            '**Actualizaciones**: todos los equipos con sus parches pendientes y un botón por fila para instalar todo lo pendiente.',
            '**Software**: el catálogo de paquetes de Chocolatey y los despliegues, sin salir del módulo.',
            '**Planes de trabajo**: solo los planes que aplican a ordenadores, no los de servidores o switches.',
          ]),
          tip(
            'Las dos salidas son «pegajosas» a propósito: al pulsarlas el menú completo vuelve y se ' +
            'queda así mientras sigas en la misma pantalla. En cuanto entras otra vez en una vista del ' +
            'módulo, vuelve a aparecer su menú.'
          ),
          warn(
            'La entrada **Est. de Trabajo** del menú general sigue existiendo y lleva al dashboard del ' +
            'módulo: no hay dos caminos distintos, solo uno que se estrecha al entrar.'
          ),
        ],
      },
      {
        id: 'listados.workstations',
        title: 'El listado de ordenadores',
        summary: 'Filtros que se combinan, informes en PDF y Excel, y qué datos manda el agente.',
        menuKey: 'workstations',
        route: '/infrastructure/workstations',
        revisedOn: '2026-11-01',
        blocks: [
          text(
            'La pantalla **Ordenadores** reúne los equipos de la empresa con su inventario, su salud ' +
            'y el estado del agente. Todo lo que ves se puede filtrar y lo filtrado se puede convertir ' +
            'en un **informe**.'
          ),
          steps([
            'La barra de filtros **combina** todo a la vez: ubicación, tipo, estado, agente, SO, sala, dominio, cifrado, parches, antivirus, riesgo y versión del agente.',
            'Para «portátiles sin BitLocker»: Tipo = **Portátil** y Cifrado = **Sin cifrar**. Para «equipos fuera de dominio»: Dominio = **Sin dominio**.',
            'El botón **Informe** abre un modal con los filtros aplicados, permite elegir columnas y genera un documento imprimible con **Guardar PDF** o una hoja de **Excel**.',
            'La búsqueda de texto se suma a los filtros y también sale en la cabecera del informe.',
          ]),
          tip(
            'El informe lleva una cabecera con la fecha de generación y los filtros exactos que se ' +
            'usaron: sirve para adjuntarlo a una auditoría o a una incidencia sin explicar cómo se sacó.'
          ),
          tip(
            'La exportación **Excel** de la cabecera y el botón **Informe → Exportar Excel** usan las ' +
            'mismas columnas y etiquetas, así que los dos ficheros se pueden comparar.'
          ),
          warn(
            'La columna **Instalación** del listado dice por dónde se instaló el agente (MSI, EXE o ' +
            'código), no cómo instalar software: para eso está **Agente → Paquetes** y la tarjeta de ' +
            'Chocolatey de la ficha.'
          ),
        ],
      },
      {
        id: 'listados.workstation_performance',
        title: 'Rendimiento del equipo',
        summary: 'CPU, memoria, discos, red y servicios del ordenador, ahora y en el histórico.',
        menuKey: 'workstations',
        route: '/infrastructure/workstations',
        revisedOn: '2026-11-01',
        blocks: [
          text(
            'La pestaña **Rendimiento** enseña cómo está el equipo por dentro, con los datos que el ' +
            'agente envía **cada minuto**. Arriba se elige el periodo: 1 hora, 6 horas, 24 horas, 3 días ' +
            'o una semana.'
          ),
          steps([
            '**Ahora mismo**: dos agujas con el CPU y la memoria, más el tiempo que lleva encendido.',
            '**Resumen del periodo**: CPU media y máxima, RAM media y número de muestras.',
            '**CPU y memoria en el tiempo**: la gráfica de cada uno; pasando el ratón por encima se lee el valor.',
            '**Discos**: barra de ocupación por volumen, con lo que queda libre.',
            '**Red**: velocidad de subida y bajada por interfaz, el total acumulado y los errores o descartes.',
            '**Servicios**: los que corren y los que están parados, con su estado.',
          ]),
          text(
            'Los datos llegan solos: no hay que recargar. El agente empuja una muestra en cada ciclo y la ' +
            'pantalla se actualiza al momento; además se refresca cada 30 s por si se pierde la conexión.'
          ),
          warn(
            'El histórico se guarda **30 días**. Pasado ese plazo las muestras se borran solas; si ' +
            'necesitas conservar algo más, exporta el informe antes.'
          ),
          tip(
            'En **General** hay una banda compacta **En vivo** con lo mismo de un vistazo. La pestaña ' +
            'Rendimiento es para mirar el detalle y el histórico.'
          ),
        ],
      },
      {
        id: 'listados.workstation_inventory',
        title: 'La ficha del ordenador: inventario, rendimiento y licencia',
        summary: 'Qué se edita a mano, qué manda el agente, cómo va el equipo en vivo y cómo se instala software.',
        menuKey: 'workstations',
        route: '/infrastructure/workstations',
        revisedOn: '2026-11-02',
        blocks: [
          text(
            'La ficha del ordenador mezcla dos mundos: lo que **escribe una persona** (código, ' +
            'usuario, ubicación, notas) y lo que **reporta el agente** (MAC, IP principal, número de ' +
            'serie, fechas y licencia). Cuando el equipo tiene agente, los datos de inventario se ven ' +
            'con un candado: no se pueden editar porque el siguiente escaneo los revertiría.'
          ),
          steps([
            'La banda **En vivo**, arriba de General, enseña **CPU, RAM, el disco más lleno y los servicios** que corren, con su mini-gráfica. Se actualiza sola con cada reporte del agente: no hay que recargar la página.',
            'En **General → Identificación**, el **✨ del Código** pide a la **nomenclatura** de la empresa el siguiente código (WST-0001…) y lo deja reservado; si el equipo aún no tiene nombre, lo rellena también.',
            'En **General → Identificación**, el número de serie sale bloqueado si hay agente.',
            'En **General → Red y agente** y en la pestaña **Red**, la MAC y la IP principal también van bloqueadas.',
            'Para añadir otra IP usa la tarjeta **IPAM — Asignaciones de IP**: la IP adicional es manual y, cuando el agente la detecte, pasará a marcarse como de inventario.',
            'La tarjeta **Sistema y antigüedad** muestra la fecha de la BIOS, la fecha de instalación del Windows actual y los años transcurridos.',
            'La **licencia de Windows** enseña el canal (OEM, Retail, Volume), los 5 últimos caracteres de la clave y el estado de activación. La clave completa no se guarda nunca.',
            'Si el equipo tiene agente 2.6.11 o superior, la pestaña **Software** incluye Chocolatey para instalar o actualizar paquetes sin salir de la ficha.',
          ]),
          warn(
            'La fecha de instalación del SO se reinicia en las actualizaciones in-place de Windows: ' +
            'sirve para saber cuándo se instaló este Windows, no para la antigüedad del hardware. ' +
            'Para eso está la **fecha de BIOS**.'
          ),
          tip(
            'Un equipo sin agente deja los campos de inventario libres: si lo diste de alta a mano, ' +
            'los puedes corregir hasta que el agente lo adopte. Sin agente tampoco hay banda **En vivo**.'
          ),
        ],
      },
      {
        id: 'listados.workstation_updates',
        title: 'Actualizaciones de Windows del equipo',
        summary: 'Qué le falta instalar al equipo, qué se instaló y si Windows Update está sano.',
        menuKey: 'racks',
        route: '/infrastructure/racks',
        revisedOn: REVISED,
        blocks: [
          text(
            'En un equipo **Windows** aparece la pestaña **Actualizaciones**. Sale de lo que ' +
            'reporta el agente al escanear: no es una consulta en vivo, así que puede tener ' +
            'unas horas de antigüedad (se refresca cada 6).'
          ),
          steps([
            'Los cuatro datos de arriba: **pendientes**, **instaladas en 30 días**, **fallidas** y **última comprobación**.',
            'La tabla **Actualizaciones disponibles** lista cada pendiente con su KB, el tipo, el tamaño y si ya está descargada o pide reinicio.',
            'Marca las que quieras y pulsa **Instalar seleccionadas**, o **Instalar todas** para ir de una vez.',
            'El **historial reciente** son los últimos parches aplicados con su fecha y resultado.',
            'Si el servicio `wuauserv` está parado, se avisa abajo: mientras siga así el equipo no recibirá nada.',
          ]),
          warn(
            'La instalación ocurre **en el equipo**: el agente descarga e instala en segundo plano y sigue ' +
            'latiendo mientras lo hace. **No reinicia solo**, aunque alguna actualización lo pida: el aviso ' +
            'queda en la ficha y decides tú cuándo.'
          ),
          warn(
            'Instalar desde la CMDB necesita el agente **2.6.10** o superior. En versiones anteriores los ' +
            'botones responden con un aviso explicando qué falta.'
          ),
          tip(
            'Un equipo con pendientes acumulados suele acabar con reinicios inesperados. Si ves ' +
            'muchos y con la pill de **reinicio pendiente**, avisa antes de que lo haga por su cuenta.'
          ),
          tip(
            'Las actualizaciones **ya no aparecen en la pestaña Software**: Windows las apunta en el ' +
            'mismo registro que los programas y un equipo con 40 parches parecía tener 40 aplicaciones. ' +
            'Su sitio es esta pestaña.'
          ),
          text(
            'Abajo, la tarjeta **Software de terceros (Chocolatey)** cubre lo que Windows Update no toca: ' +
            'Chrome, Vivaldi, 7-Zip, Acrobat. **Actualizar paquetes del equipo** lanza ' +
            '`choco upgrade all` en ese equipo y actualiza de golpe todo lo instalado con Chocolatey. ' +
            'Lo que se instaló a mano no lo toca: para eso está el catálogo de la pestaña **Software**.'
          ),
        ],
      },
      {
        id: 'listados.agent_ad',
        title: 'Active Directory del equipo',
        summary: 'Dominio, OU, grupos, GPO y BitLocker sin entrar por RDP.',
        menuKey: 'racks',
        route: '/infrastructure/racks',
        revisedOn: REVISED,
        blocks: [
          text(
            'En un equipo **Windows** unido a dominio aparece la pestaña **Active Directory**. ' +
            'Enseña el equipo visto desde el dominio: con qué controlador habla, dónde está su ' +
            'objeto y qué directivas le están aplicando.'
          ),
          steps([
            'Arriba, cuatro datos: si está unido, el dominio, el **DC autenticador** y cuándo se consultó.',
            '**Información de dominio** y **Controlador de dominio**: canal seguro, estado de confianza, SID del equipo, sitio AD, autenticación y DNS configurado.',
            '**Ubicación en Active Directory**: el `Distinguished Name` y el árbol hasta el equipo.',
            '**Directivas de grupo**: qué GPO se aplican, a qué contenedor están vinculadas y si su aplicación fue correcta.',
            '**Grupos del equipo** en AD y las **claves de recuperación de BitLocker** publicadas.',
          ]),
          tip(
            'Los botones **Consultar AD**, **Forzar actualización GPO** y **Generar informe RSoP** se ' +
            'ejecutan en el equipo a través del agente; el resultado de cada uno queda en la pestaña ' +
            '**Agente**, con su log.'
          ),
          warn(
            'Las claves de BitLocker solo se leen si el equipo tiene permiso sobre ' +
            '`msFVE-RecoveryInformation`; si no, la tarjeta lo dice. La contraseña de recuperación ' +
            'no se muestra en la ficha: queda en el inventario para soporte.'
          ),
        ],
      },
      {
        id: 'listados.bulk_edit',
        title: 'Editar muchos registros de golpe',
        summary: '«✏️ Editar campos»: cambiar un valor en toda la selección, como en Odoo.',
        menuKey: 'racks',
        route: '/infrastructure/racks',
        revisedOn: REVISED,
        blocks: [
          text(
            'Con varias filas seleccionadas aparecen **📍 Ubicación / rack** y **✏️ Editar campos** ' +
            'en la barra de selección. La segunda cambia un campo **no único** en todos los registros ' +
            'seleccionados sin abrir ficha por ficha.'
          ),
          steps([
            'Marca las casillas de las filas (o **todo lo visible** en la cabecera).',
            'Pulsa **✏️ Editar campos**.',
            'Marca solo los campos que quieras cambiar y escribe su valor nuevo. Lo que no marques no se toca.',
            '**Aplicar a los seleccionados**. El resumen dice cuántos se actualizaron y qué filas fallaron, si alguna.',
          ]),
          tip('El botón «✏️ Editar campos» está en los listados de servidores, cabinas, switches/patch panels/PDUs, chasis, firewalls, ordenadores, impresoras, periféricos, teléfonos IP, puntos de acceso y máquinas virtuales.'),
          warn('No se cambian en masa los identificadores (nombre, código, nº de serie, MAC, IP). Eso sigue siendo ficha a ficha, y la ubicación/rack tiene su propio botón con validación de hueco y sede.'),
        ],
      },
      {
        id: 'listados.mobile',
        title: 'Los listados en el móvil',
        summary: 'Qué se esconde y qué se mantiene.',
        menuKey: 'racks',
        route: '/infrastructure/racks',
        revisedOn: REVISED,
        blocks: [
          text('En el móvil la tabla se desplaza en horizontal y la barra se queda con lo esencial.'),
          steps([
            'El buscador sigue arriba: es la forma más rápida de encontrar una fila.',
            'Las acciones de fila se tocan sin abrir la ficha.',
            'Los botones de la barra pierden el texto y se quedan con el icono.',
          ]),
          warn('Para trabajos de varias filas (edición masiva, importar) usa un ordenador.'),
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────
  {
    id: 'perfil',
    title: 'Mi perfil',
    summary: 'Tu aspecto, tu idioma y tu identidad.',
    icon: '👤',
    topics: [
      {
        id: 'perfil.open',
        title: 'Dónde está tu perfil',
        summary: 'El avatar del topbar.',
        menuKey: 'dashboard',
        route: '/dashboard',
        revisedOn: REVISED,
        blocks: [
          text('Tu perfil se abre desde el **avatar** de la esquina superior derecha.'),
          steps([
            'Pulsa el avatar.',
            'Elige **Mi perfil**.',
            'Desde ahí también puedes cerrar sesión.',
          ]),
        ],
      },
      {
        id: 'perfil.appearance',
        title: 'Colores y estilo',
        summary: 'Estilo (forma), paleta (color), movimiento y densidad de las tablas.',
        menuKey: 'dashboard',
        route: '/dashboard',
        revisedOn: REVISED,
        blocks: [
          text(
            'El aspecto tiene dos ejes independientes: el **estilo** (forma: radios, sombras, densidad) y la ' +
            '**paleta** (color). Se aplica al momento y se guarda en tu preferencia, no en la de la empresa.'
          ),
          shot('perfil/01-aspecto.png', 'Estilos disponibles: Classic, Lumina, Lumina Dark, Onyx y Nordic.'),
          steps([
            'Entra en **Mi perfil**.',
            'En **Estilo** elige la forma: **Classic**, **Lumina**, **Lumina Dark**, **Onyx** o **Nordic**.',
            'Elige la **paleta** de acento y el **movimiento** del fondo.',
            'Ajusta la **densidad de las tablas**: Automática, Cómoda o Compacta.',
            '¿Peleado con el aspecto de la empresa? **Usar el de mi empresa** o **Restablecer**.',
          ]),
          sandbox('theme'),
          tip('Onyx y Nordic son oscuros; Lumina es claro. El estilo marca la forma; la paleta, sólo el color.'),
        ],
      },
      {
        id: 'perfil.language',
        title: 'Idioma',
        summary: 'Dentro de «Aspecto de la aplicación»; se guarda en tu perfil.',
        menuKey: 'dashboard',
        route: '/dashboard',
        revisedOn: REVISED,
        blocks: [
          text(
            'El idioma está al final de **Aspecto de la aplicación**, no en una tarjeta aparte. Se guarda en ' +
            'tu usuario, así que viaja contigo a cualquier equipo.'
          ),
          shot('perfil/02-idioma.png', 'Movimiento, densidad de tablas, idioma y el bloque de identidad.'),
          steps([
            'Abre **Mi perfil → Aspecto de la aplicación**.',
            'Baja hasta **Idioma** y elige el que quieras: la interfaz cambia al momento.',
            'Entra desde otro equipo: seguirá en el mismo idioma.',
          ]),
          warn('Si un texto aparece sin traducir es un texto nuevo: avísalo con el Feedback Hub.'),
        ],
      },
      {
        id: 'perfil.identity',
        title: 'Tu identidad vive en el IdP',
        summary: 'Nombre, correo, contraseña y doble factor: fuera del CMDB.',
        menuKey: 'dashboard',
        route: '/dashboard',
        revisedOn: REVISED,
        blocks: [
          text(
            'El CMDB no guarda tu contraseña. El nombre, el correo, la contraseña y el doble factor se ' +
            'gestionan en el **IdP de Adder**, que es la fuente de verdad para todas las aplicaciones.'
          ),
          steps([
            'Entra en **Mi perfil**.',
            'En **Mi identidad**, pulsa **Gestionar mi identidad en el IdP**.',
            'Se abre el perfil del IdP en una pestaña nueva.',
          ]),
          tip('Si no puedes cambiar la contraseña, es un problema del IdP, no del CMDB: no abras ticket al sitio equivocado.'),
        ],
      },
    ],
  },
  {
    id: 'planes',
    title: 'Planes de trabajo',
    summary: 'Tareas que hay que completar en muchos elementos a la vez, con su avance, su fecha de cambio y quién lo hizo.',
    icon: '📋',
    topics: [
      {
        id: 'planes.que-son',
        title: 'Qué es un plan de trabajo',
        summary: 'Una tarea de campo repartida en pasos, las tres formas de darse un paso por hecho y las condiciones del alcance.',
        menuKey: 'work-plans',
        route: '/work-plans',
        revisedOn: '2026-11-02',
        blocks: [
          text(
            'Un **plan de trabajo** es una tarea que afecta a muchos elementos a la vez: «cifrar todo el ' +
            'parque», «ampliar a 16 GB», «preparar los portátiles nuevos». El plan se divide en **pasos**, y ' +
            'cada paso declara cómo se da por hecho:'
          ),
          steps([
            '**Automático por inventario** — el propio inventario del elemento lo dice (`bitlocker_state`, `ram_gb`, `part_of_domain`, `agent_version`…). El sistema lo recalcula solo.',
            '**Automático por comando** — el paso lanza un comando al agente y su resultado decide.',
            '**Manual** — lo marca una persona, y se guarda **quién** lo hizo.',
          ]),
          text(
            'El estado se guarda **por elemento y por paso**, con la fecha del último cambio. Eso permite ' +
            'responder con datos una pregunta que hasta ahora no se podía: «este equipo, ¿desde cuándo ' +
            'cumple?». La respuesta está en la ficha y en la matriz del plan.'
          ),
          text(
            'Un plan no mira solo ordenadores: puede aplicar a **servidores, máquinas virtuales, ' +
            'hipervisores, firewalls, routers, impresoras y periféricos**. El botón **Alcance** elige los ' +
            'tipos y, si hace falta, una **lista concreta** de elementos.'
          ),
          text(
            'El alcance también admite **condiciones**. En un plan de estaciones se puede elegir ' +
            '**solo portátiles** o **solo sobremesa**, y añadir condiciones libres sobre el inventario. ' +
            'Un **plan renove** se monta así: tipo de equipo «Solo portátiles» y la condición ' +
            '**fecha de compra · menor o igual que · 2020-01-01**. Solo entran los equipos viejos; el ' +
            'resto del parque no aparece en la matriz.'
          ),
          tip(
            'Si portátiles y sobremesa necesitan tareas distintas, haz **dos planes**; si comparten las ' +
            'mismas, un solo plan con «Todos» sirve para ambos.'
          ),
          warn(
            'Un paso automático **no se puede marcar a mano**: si el inventario dice que no lo cumple, ' +
            'marcarlo sería mentir. La excepción son los pasos con «si no hay dato, se puede marcar»: ' +
            'el equipo recién llegado que aún no reporta si está en el dominio. Ahí sí, y se respeta ' +
            'mientras siga sin haber dato.'
          ),
          tip('Los pasos automáticos usan los campos que ya reporta el agente: no hay que instalar nada nuevo.'),
        ],
      },
      {
        id: 'planes.pantalla',
        title: 'La pantalla de control',
        summary: 'Listado con avance, matriz elemento × paso a pantalla completa, orden de columnas, edición de pasos, alta rápida de equipos y procedimientos.',
        menuKey: 'work-plans',
        route: '/work-plans',
        revisedOn: '2026-11-02',
        blocks: [
          text(
            '**Planes de trabajo** vive en la barra de arriba, junto a Dashboard: es una tarea transversal, ' +
            'no una parte de la infraestructura física. Se ve el estado global: cuántos planes hay en curso, ' +
            'cuántos elementos afectan y cuántas tareas están hechas.'
          ),
          steps([
            'Pulsa **Nuevo plan** y elige una plantilla: trae ya escritos los campos de inventario.',
            'Abre una fila: el plan ocupa **toda la pantalla** con la matriz elemento × paso.',
            'Cada celda verde lleva la **fecha desde la que se cumple**; si la marcó alguien, su nombre.',
            '**Recalcular** vuelve a evaluar el inventario en ese momento, sin esperar al siguiente escaneo.',
            '**Alcance** cambia a qué tipos aplica, permite limitarlo a unos elementos concretos, elegir portátiles o sobremesa y añadir condiciones.',
            'En la cabecera de cada columna, las **flechas** mueven el paso a izquierda o derecha y el **lápiz** lo edita sin borrarlo.',
          ]),
          text(
            'La matriz abre por defecto con el filtro **Solo los que les faltan cosas**: se ven los ' +
            'elementos con algún paso pendiente y se esconde el resto. El chip del filtro dice cuántos ' +
            'son y lo desactiva cuando hace falta revisar el parque completo.'
          ),
          text(
            'La tarjeta **Empezar en blanco** ya no crea un plan sin alcance: marca ahí mismo qué tipos de ' +
            'elemento va a mirar. Si marcas **Equipo de rack**, aparecen sus tipos (servidor, switch, ' +
            'firewall, router, almacenamiento, PDU, SAI, panel, chasis) y el plan solo evaluará esos: ' +
            'un plan de «firmware de switches» deja de contar los servidores.'
          ),
          text(
            'Desde un plan de estaciones puedes pulsar **Crear equipos** y dar de alta varios ' +
            'indicando **cuántos** son. La pantalla enseña los **códigos que va a asignar la ' +
            'nomenclatura** para que los revises o corrijas, y crea con **código = nombre**, que es ' +
            'como se identifica el equipo. Nacen con la ficha a medias: los pasos del plan aparecen ' +
            'pendientes, así que se ve a la primera qué les falta (agente, cifrado, dominio…).'
          ),
          text(
            'Cada paso puede llevar su **procedimiento**. En el diálogo del paso eliges un documento ' +
            'de la CMDB o pulsas **Crear y vincular** para crear uno en borrador y escribirlo con calma ' +
            'en la biblioteca. El icono de documento aparece junto al título de la columna y en la ' +
            'tarjeta del equipo, y abre el procedimiento en otra pestaña.'
          ),
          text(
            '¿Repites siempre las mismas tareas? Monta el plan una vez y pulsa **Guardar como plantilla**. ' +
            'Aparecerá en «Nuevo plan» junto a las de la casa, y se puede renombrar o borrar desde ahí.'
          ),
          text(
            'En la pestaña **General** de cada ordenador aparece la tarjeta **Planes de trabajo** con los ' +
            'pasos que le faltan a esa máquina concreta. Si la empresa no tiene planes, la tarjeta no se ' +
            'pinta.'
          ),
          tip('El filtro **Objetivo** agrupa los planes por familia (seguridad, hardware, altas…).'),
        ],
      },
    ],
  },
];

/**
 * Entradas del menú que todavía no tienen tema propio.
 *
 * El test de cobertura (`registry.test.js`) obliga a que esta lista sólo
 * encoja: si añades un módulo al menú y no lo documentas, la build falla.
 * Cuando documentes uno, quítalo de aquí.
 */
export const COVERAGE_PENDING = [
  'locations', 'contacts', 'servers', 'chassis', 'storage', 'pdus', 'ups',
  'patch-panels', 'ip-phones', 'printers', 'peripherals',
  'access-points', 'datacenter-diagram', 'power-canvas', 'switches', 'firewalls',
  'network-canvas', 'network-topology', 'network-discovered', 'connections-matrix',
  'cabling-designer', 'cabling-wiring', 'cabling-map', 'cabling-outlets',
  'cabling-canvas', 'networks-dashboard', 'networks-blocks', 'networks-subnets',
  'networks-explorer', 'networks-assign', 'networks-vlans', 'networks-ips',
  'networks-vips', 'networks-dhcp', 'wifi-dashboard', 'wifi-topology', 'wifi-map',
  'wifi-controllers', 'wifi-aps', 'wifi-radius', 'wan-operators', 'wan-circuits',
  'wan-routers', 'cameras-topology', 'cameras-map', 'cameras-recorders',
  'cameras-list', 'dns-domains', 'certificates', 'virt-overview', 'virt-topology',
  'virt-hypervisors', 'virt-clusters', 'virt-datastores', 'virt-vswitches',
  'virt-vms', 'virt-cloud', 'services-designer', 'services-list', 'services-depmap',
  'service-farms', 'backup-policies', 'db-instances', 'licenses', 'web-sites',
  'roles', 'naming', 'device-catalog', 'setup', 'manual-cmdb', 'feedback-hub',
  'vulnerabilities', 'dns-providers', 'agent-mapping',
  'company-docs', 'platform-admin',
];

// ── Índices (se construyen una vez) ───────────────────────────────────────

const TOPIC_INDEX = new Map();
ACADEMY_CHAPTERS.forEach((chapter) => {
  chapter.topics.forEach((topic) => {
    TOPIC_INDEX.set(topic.id, { ...topic, chapterId: chapter.id, chapterTitle: chapter.title });
  });
});

const TOPICS_BY_MENU_KEY = new Map();
ACADEMY_CHAPTERS.forEach((chapter) => {
  chapter.topics.forEach((topic) => {
    if (!topic.menuKey) return;
    const list = TOPICS_BY_MENU_KEY.get(topic.menuKey) || [];
    list.push(topic.id);
    TOPICS_BY_MENU_KEY.set(topic.menuKey, list);
  });
});

/** Tema completo (con su capítulo) o null. */
export function getTopic(topicId) {
  return TOPIC_INDEX.get(topicId) || null;
}

/** Capítulo al que pertenece un tema. */
export function getChapterOfTopic(topicId) {
  const topic = TOPIC_INDEX.get(topicId);
  if (!topic) return null;
  return ACADEMY_CHAPTERS.find((chapter) => chapter.id === topic.chapterId) || null;
}

/** Tema que documenta una entrada del menú (el primero declarado). */
export function getTopicForMenuKey(menuKey) {
  const ids = TOPICS_BY_MENU_KEY.get(menuKey);
  return ids && ids.length ? getTopic(ids[0]) : null;
}

/** Todos los temas en orden de lectura. */
export function allTopics() {
  return ACADEMY_CHAPTERS.flatMap((chapter) =>
    chapter.topics.map((topic) => ({ ...topic, chapterId: chapter.id, chapterTitle: chapter.title }))
  );
}

/** Búsqueda sencilla: título, resumen y texto de los bloques. */
export function searchTopics(query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return allTopics();
  return allTopics().filter((topic) => {
    const haystack = [
      topic.title,
      topic.summary,
      topic.chapterTitle,
      ...(topic.blocks || []).map((block) => block.body || (block.items || []).join(' ') || block.caption || ''),
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(q);
  });
}
